import {
  Box,
  Fab,
  InputAdornment,
  Paper,
  Tooltip,
  Typography,
} from "@mui/material";
import BadgeOutlinedIcon from "@mui/icons-material/BadgeOutlined";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import SchoolRoundedIcon from "@mui/icons-material/SchoolRounded";
import { PrimaryButton } from "./PrimaryButton";
import { StandardTextField } from "./StandardTextField";
import { FC, useState } from "react";

interface SearchFormProps {
  onClick: (dni: string) => Promise<void>;
}

export const SearchForm: FC<SearchFormProps> = ({
  onClick,
}: SearchFormProps) => {
  const [dni, setDni] = useState<string>("");

  return (
    <Paper
      className="focus-search-card"
      component="section"
      elevation={0}
      sx={{
        width: "100%",
        maxWidth: 720,
        overflow: "hidden",
        border: "1px solid rgba(1, 77, 223, 0.12)",
        borderRadius: { xs: 3.5, sm: 5 },
        boxShadow: "0 28px 80px rgba(10, 34, 156, 0.15)",
      }}
    >
      <Box
        sx={{
          position: "relative",
          overflow: "hidden",
          px: { xs: 3, sm: 5 },
          py: { xs: 3.5, sm: 4.5 },
          color: "common.white",
          background:
            "linear-gradient(128deg, #0A229C 0%, #014DDF 58%, #0008DE 100%)",
          "&::before": {
            content: '""',
            position: "absolute",
            width: 210,
            height: 210,
            borderRadius: "50%",
            top: -130,
            right: -35,
            border: "38px solid rgba(0, 217, 255, 0.2)",
          },
          "&::after": {
            content: '""',
            position: "absolute",
            width: 150,
            height: 150,
            borderRadius: 5,
            right: 80,
            bottom: -110,
            transform: "rotate(35deg)",
            bgcolor: "rgba(108, 92, 231, 0.24)",
          },
        }}
      >
        <Typography
          variant="h3"
          component="h1"
          sx={{
            position: "relative",
            zIndex: 1,
            mt: 1.5,
            maxWidth: 560,
            fontSize: { xs: 30, sm: 43 },
            fontWeight: 700,
            lineHeight: 1.12,
            letterSpacing: "-0.035em",
          }}
        >
          ¡Bienvenido!
        </Typography>
        <Typography
          sx={{
            position: "relative",
            zIndex: 1,
            mt: 1.5,
            maxWidth: 520,
            color: "rgba(255, 255, 255, 0.78)",
            lineHeight: 1.65,
          }}
        >
          Consulta tus certificados y constancias emitidas por FOCUS.
        </Typography>
      </Box>

      <Box component="form" noValidate sx={{ p: { xs: 3, sm: 5 } }}>
        <Typography
          component="label"
          htmlFor="dni"
          sx={{ display: "block", mb: 1, fontSize: 13, fontWeight: 600 }}
        >
          DNI o carné de extranjería
        </Typography>
        <StandardTextField
          variant="outlined"
          fullWidth
          id="dni"
          placeholder="Ingresa tu DNI o CE"
          name="dni"
          autoComplete=""
          autoFocus
          value={dni}
          onChange={(v) => setDni(v.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <BadgeOutlinedIcon color="primary" />
              </InputAdornment>
            ),
          }}
        />
        <PrimaryButton
          fullWidth
          variant="contained"
          className="Button"
          startIcon={<SearchRoundedIcon />}
          onClick={() => onClick(dni)}
          sx={{ mt: 1.5, minHeight: 52, fontSize: 15 }}
        >
          Consultar
        </PrimaryButton>
      </Box>
    </Paper>
  );
};

export const MoreCoursesOrWorkshops = ({ message }: { message: string }) => (
  <Paper
    component="aside"
    elevation={0}
    sx={{
      width: "100%",
      maxWidth: 760,
      mx: "auto",
      p: { xs: 2.5, sm: 3 },
      display: "flex",
      alignItems: "center",
      gap: 2,
      border: "1px solid rgba(108, 92, 231, 0.2)",
      borderRadius: 3,
      bgcolor: "rgba(108, 92, 231, 0.06)",
    }}
  >
    <Box
      sx={{
        width: 48,
        height: 48,
        display: "grid",
        placeItems: "center",
        flexShrink: 0,
        borderRadius: 2.5,
        color: "secondary.main",
        bgcolor: "rgba(108, 92, 231, 0.12)",
      }}
    >
      <SchoolRoundedIcon />
    </Box>
    <Box>
      <Typography sx={{ color: "text.primary", fontWeight: 700 }}>
        Sigue aprendiendo con Focus
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
        {message}
      </Typography>
    </Box>
  </Paper>
);

export const FloatingWhatsApp = () => (
  <Tooltip title="Descubre nuestros cursos" placement="left" arrow>
    <Fab
      component="a"
      target="_blank"
      rel="noreferrer"
      href="https://whatsapp.com/channel/0029VaDySzwFHWq0sH7IvR12"
      aria-label="Descubre nuestros cursos por WhatsApp"
      sx={{
        position: "fixed",
        right: { xs: 18, sm: 28 },
        bottom: { xs: 18, sm: 28 },
        zIndex: 1300,
        width: { xs: 56, sm: 64 },
        height: { xs: 56, sm: 64 },
        p: 0,
        bgcolor: "transparent",
        boxShadow: "0 14px 34px rgba(31, 198, 83, 0.38)",
        animation: "focusWhatsAppIn 420ms ease-out both",
        transition: "transform 180ms ease, box-shadow 180ms ease",
        "@keyframes focusWhatsAppIn": {
          from: { opacity: 0, transform: "translateY(18px) scale(0.88)" },
          to: { opacity: 1, transform: "translateY(0) scale(1)" },
        },
        "&:hover": {
          bgcolor: "transparent",
          transform: "translateY(-3px)",
          boxShadow: "0 18px 40px rgba(31, 198, 83, 0.46)",
        },
      }}
    >
      <Box
        component="img"
        src="./icons/whatsapp-channel-icon.png"
        alt=""
        sx={{ width: "100%", height: "100%" }}
      />
    </Fab>
  </Tooltip>
);
